import { useCallback, useEffect, useState } from 'react';
import { Currency, ResponseError, Response } from '@ui/src';
import { Balance } from '../../types';
import {
  useChangeAccountBalanceStatusMutation,
  useLazyGetAccountBalancesQuery,
} from '../../store/services';

const useAvailableCurrenciesFunctions = ({
  accountId,
}: {
  accountId?: string;
}) => {
  const [balances, setBalances] = useState<Balance[]>([]);
  const [blockPopupVisible, setBlockPopupVisible] = useState(false);
  const [enablePopupVisible, setEnablePopupVisible] = useState(false);
  const [selectedBlockCurrency, setSelectedBlockCurrency] = useState<
    Currency | undefined
  >(undefined);
  const [selectedEnableCurrency, setSelectedEnableCurrency] = useState<
    Currency | undefined
  >(undefined);
  const [error, setError] = useState('');

  const [onEnableBalance, { isLoading: enableInProgress }] =
    useChangeAccountBalanceStatusMutation();
  const [onDisableBalance, { isLoading: disableInProgress }] =
    useChangeAccountBalanceStatusMutation();
  const [getBalance, { isLoading, isFetching }] =
    useLazyGetAccountBalancesQuery();

  const onEnableBalanceAction = async () => {
    const response = (await onEnableBalance({
      id: accountId as string,
      body: {
        currencyId: selectedEnableCurrency?.id as string,
        active: true,
      },
    })) as {
      error: ResponseError | { data: { message: string } };
      data?: { data?: Balance };
    };

    if (response?.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setError(response?.error?.data?.message[0]?.value as string);
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setError(response?.error?.data?.message);
      } else {
        setError('Internal Server Error. Try Again');
      }
    } else if (response?.data && response?.data?.data) {
      const nextBalances = ([] as Balance[]).concat(balances);
      nextBalances.push(response.data.data);

      setBalances(nextBalances);
      setSelectedEnableCurrency(undefined);
      setEnablePopupVisible(false);
    }
  };

  const onDisableBalanceAction = async () => {
    const response = (await onDisableBalance({
      id: accountId as string,
      body: {
        currencyId: selectedBlockCurrency?.id as string,
        active: false,
      },
    })) as {
      error: ResponseError | { data: { message: string } };
      data?: { data?: Balance };
    };

    if (response?.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setError(response?.error?.data?.message[0]?.value as string);
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setError(response?.error?.data?.message);
      } else {
        setError('Internal Server Error. Try Again');
      }
    } else if (response?.data && response?.data?.data) {
      setBalances(
        balances?.filter((item) => item.id !== response?.data?.data?.id),
      );
      setSelectedBlockCurrency(undefined);
      setBlockPopupVisible(false);
    }
  };

  const getBalancesAction = useCallback(async (id: string) => {
    const response = (await getBalance(id)) as { data: Response<Balance> };

    if (response && response?.data?.data) {
      setBalances(response?.data?.data?.edges?.map((item) => item.node));
    }
  }, []);

  useEffect(() => {
    if (accountId) {
      getBalancesAction(accountId);
    }
  }, [accountId]);

  return {
    blockPopupVisible,
    enablePopupVisible,
    error,
    enableInProgress,
    disableInProgress,
    isLoading,
    isFetching,
    onEnableBalanceAction,
    onDisableBalanceAction,
    balances,
    setBlockPopupVisible,
    setSelectedBlockCurrency,
    setEnablePopupVisible,
    setSelectedEnableCurrency,
    selectedBlockCurrency,
    setError,
    selectedEnableCurrency,
  };
};

export default useAvailableCurrenciesFunctions;
